import { Component, OnInit, inject, input, output, signal, computed, ChangeDetectionStrategy, effect, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import Swal from 'sweetalert2';
import { AutomatizePeriodService } from '../../services/automatize-period.service';
import { PeriodService } from '../../../billing-management/services/period.service';

@Component({
  selector: 'app-automatize-period',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './automatize-period.component.html',
  changeDetection: ChangeDetectionStrategy.Eager
})
export class AutomatizePeriodComponent implements OnInit {
  private automatizePeriodService = inject(AutomatizePeriodService);
  private periodService = inject(PeriodService);

  canRun = input(false);
  ran = output<{ id?: number; year?: number; period?: number } | null>();
  dismiss = output<void>();

  config = signal({
    isActive: false,
    closeDay: 10,
    runHour: 6,
    runMinute: 0,
    mailHour: 9,
    mailMinute: 0,
    lastRunAt: null as string | null,
    lastRunStatus: null as string | null,
    lastRunMessage: null as string | null,
    lastMailAt: null as string | null,
    lastMailStatus: null as string | null,
    lastMailMessage: null as string | null
  });
  isSaving = signal(false);
  isRunning = signal(false);
  runDay = computed(() => Math.min(28, Number(this.config().closeDay || 10) + 1));

  constructor() {
    effect(() => {
      const rev = this.periodService.revision();
      if (!rev) return;
      untracked(() => this.load());
    });
  }

  ngOnInit(): void {
    this.load();
  }

  timeLabel(hour: number | string | null | undefined, minute: number | string | null | undefined = 0): string {
    const hh = String(Math.min(23, Math.max(0, Number(hour) || 0))).padStart(2, '0');
    const mm = String(Math.min(59, Math.max(0, Number(minute) || 0))).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  load(): void {
    this.automatizePeriodService.get().subscribe({
      next: (res) => this.patch(res),
      error: () => {}
    });
  }

  patch(res: any): void {
    this.config.set({
      isActive: !!res?.isActive,
      closeDay: Number(res?.closeDay ?? 10),
      runHour: Number(res?.runHour ?? 6),
      runMinute: Number(res?.runMinute ?? 0),
      mailHour: Number(res?.mailHour ?? 9),
      mailMinute: Number(res?.mailMinute ?? 0),
      lastRunAt: res?.lastRunAt ?? null,
      lastRunStatus: res?.lastRunStatus ?? null,
      lastRunMessage: res?.lastRunMessage ?? null,
      lastMailAt: res?.lastMailAt ?? null,
      lastMailStatus: res?.lastMailStatus ?? null,
      lastMailMessage: res?.lastMailMessage ?? null
    });
  }

  onToggle(active: boolean): void {
    this.config.update(c => ({ ...c, isActive: active }));
  }

  onCloseDayChange(day: number | string): void {
    this.config.update(c => ({ ...c, closeDay: Number(day) }));
  }

  onRunHourChange(hour: number | string): void {
    this.config.update(c => ({ ...c, runHour: Number(hour) }));
  }

  onRunMinuteChange(minute: number | string): void {
    this.config.update(c => ({ ...c, runMinute: Number(minute) }));
  }

  onMailHourChange(hour: number | string): void {
    this.config.update(c => ({ ...c, mailHour: Number(hour) }));
  }

  onMailMinuteChange(minute: number | string): void {
    this.config.update(c => ({ ...c, mailMinute: Number(minute) }));
  }

  save(): void {
    const c = this.config();
    this.isSaving.set(true);
    this.automatizePeriodService.update({
      isActive: c.isActive,
      closeDay: c.closeDay,
      runHour: c.runHour,
      runMinute: c.runMinute,
      mailHour: c.mailHour,
      mailMinute: c.mailMinute
    }).subscribe({
      next: (res) => {
        this.patch(res);
        this.isSaving.set(false);
        Swal.fire({
          title: 'Configuración guardada',
          text: c.isActive
            ? `El día ${this.runDay()} a las ${this.timeLabel(c.runHour, c.runMinute)} se cierra el periodo. Si cae sábado, domingo o feriado, pasa al siguiente día hábil. Los correos salen a las ${this.timeLabel(c.mailHour, c.mailMinute)}.`
            : 'La automatización quedó desactivada. El cierre sigue siendo manual.',
          icon: 'success',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 5000
        });
      },
      error: (err) => {
        this.isSaving.set(false);
        Swal.fire('Error', err.error?.message || err.error?.error || 'No se pudo guardar', 'error');
      }
    });
  }

  runNow(): void {
    const c = this.config();
    Swal.fire({
      title: '¿Ejecutar ahora?',
      text: `Se generarán cobros, se cerrará el periodo y se abrirá el siguiente. Los correos salen a las ${this.timeLabel(c.mailHour, c.mailMinute)}; si esa hora ya pasó, se envían ahora.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, ejecutar',
      cancelButtonText: 'Cancelar'
    }).then((r) => {
      if (!r.isConfirmed) return;
      this.isRunning.set(true);
      this.automatizePeriodService.run().subscribe({
        next: (res) => {
          this.isRunning.set(false);
          this.load();
          this.ran.emit(res?.openedPeriod ?? res?.closedPeriod ?? null);
          Swal.fire({
            title: 'Automatización ejecutada',
            text: res?.message || 'Listo.',
            icon: 'success',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 6000
          });
        },
        error: (err) => {
          this.isRunning.set(false);
          this.load();
          Swal.fire('Error', err.error?.error || err.error?.message || 'No se pudo ejecutar', 'error');
        }
      });
    });
  }

  lastRunLabel(): string {
    const c = this.config();
    const parts: string[] = [];
    if (c.lastRunAt) {
      const when = new Date(c.lastRunAt).toLocaleString('es-CL');
      const status = c.lastRunStatus === 'SUCCESS' ? 'Cierre OK' : (c.lastRunStatus || 'Cierre');
      parts.push(`${status} · ${when}${c.lastRunMessage ? ' · ' + c.lastRunMessage : ''}`);
    }
    if (c.lastMailAt) {
      const when = new Date(c.lastMailAt).toLocaleString('es-CL');
      const status = c.lastMailStatus === 'SUCCESS' ? 'Correos OK' : (c.lastMailStatus || 'Correos');
      parts.push(`${status} · ${when}${c.lastMailMessage ? ' · ' + c.lastMailMessage : ''}`);
    }
    return parts.length ? parts.join(' | ') : 'Todavía no hay ejecuciones.';
  }
}
